import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, ExternalLink, Calendar, Users } from 'lucide-react'

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  venue: string
  thumbnail: string
  keywords: string[]
}

type GalleryData = Record<string, Gallery>

export const WeddingVenuesPage: React.FC = () => {
  const [galleries, setGalleries] = useState<GalleryData>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch('/data/galleries.json')
        const data = await response.json()
        setGalleries(data)
      } catch (error) {
        console.error('Error fetching galleries:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGalleries()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Filter wedding venues only
  const weddingVenues = Object.entries(galleries).filter(([_, gallery]) => 
    gallery.category === 'wedding'
  )

  const venueDetails = [
    {
      id: 'grace-cathedral',
      name: 'Grace Cathedral',
      location: 'San Francisco, CA',
      description: 'Iconic Gothic cathedral with stunning architecture and sacred atmosphere',
      capacity: '100-300 guests',
      style: 'Gothic, Religious, Traditional',
      features: ['Gothic Architecture', 'Sacred Atmosphere', 'City Location', 'Historic Venue']
    },
    {
      id: 'stanford-memorial-church',
      name: 'Stanford Memorial Church',
      location: 'Stanford, CA',
      description: 'Beautiful Romanesque church on the Stanford University campus',
      capacity: '200-400 guests',
      style: 'Romanesque, Academic, Traditional',
      features: ['University Setting', 'Romanesque Design', 'Academic Prestige', 'Historic Architecture']
    },
    {
      id: 'vine-hill-house',
      name: 'Vine Hill House',
      location: 'Sebastopol, CA',
      description: 'Rustic vineyard venue with rolling hills and wine country charm',
      capacity: '75-150 guests',
      style: 'Rustic, Vineyard, Outdoor',
      features: ['Wine Country', 'Rolling Hills', 'Outdoor Ceremony', 'Rustic Charm']
    },
    {
      id: 'willow-heights-mansion',
      name: 'Willow Heights Mansion',
      location: 'Morgan Hill, CA',
      description: 'Elegant mansion with manicured gardens and romantic ambiance',
      capacity: '100-200 guests',
      style: 'Elegant, Garden, Traditional',
      features: ['Manicured Gardens', 'Elegant Mansion', 'Romantic Setting', 'Luxury Venue']
    },
    {
      id: 'point-reyes-seashore-lodge',
      name: 'Point Reyes Seashore Lodge',
      location: 'Point Reyes, CA',
      description: 'Coastal venue with ocean views and natural beauty',
      capacity: '50-120 guests',
      style: 'Coastal, Natural, Intimate',
      features: ['Ocean Views', 'Natural Beauty', 'Coastal Setting', 'Intimate Atmosphere']
    },
    {
      id: 'thomas-estate',
      name: 'Thomas Estate',
      location: 'California',
      description: 'Private estate with lush gardens and timeless elegance',
      capacity: '80-180 guests',
      style: 'Private, Garden, Elegant',
      features: ['Private Estate', 'Lush Gardens', 'Timeless Elegance', 'Exclusive Setting']
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Wedding Venues
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Discover beautiful venues throughout the San Francisco Bay Area where we've captured unforgettable moments
          </p>
        </div>
      </section>

      {/* Featured Venue Galleries */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            Our Featured Wedding Venue Galleries
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Click on any venue thumbnail below to view our complete wedding photography gallery from that location.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {weddingVenues.map(([galleryId, gallery]) => (
              <Link
                key={galleryId}
                to={`/gallery/${galleryId}`}
                className="group block bg-white shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={gallery.thumbnail}
                    alt={gallery.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-light text-gray-900 group-hover:text-gray-700 transition-colors duration-200">
                      {gallery.venue}
                    </h3>
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-200" />
                  </div>
                  
                  <div className="flex items-center text-gray-600 mb-3">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span className="text-sm">{gallery.location}</span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">{gallery.subtitle}</p>
                  
                  <div className="flex flex-wrap gap-1">
                    {gallery.keywords?.slice(0, 4).map((keyword, index) => (
                      <span key={index} className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Venue Details Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">
            Venue Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {venueDetails.map((venue) => (
              <div key={venue.id} className="bg-white shadow-sm border border-gray-100 p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-light text-gray-900">{venue.name}</h3>
                  <Link to={`/gallery/${venue.id}`}>
                    <ExternalLink className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors duration-200" />
                  </Link>
                </div>
                
                <div className="flex items-center text-gray-600 mb-3">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span className="text-sm">{venue.location}</span>
                </div>

                <p className="text-gray-700 leading-relaxed mb-4">
                  {venue.description}
                </p>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium text-gray-900">Capacity: </span>
                    <span className="text-gray-600 ml-1">{venue.capacity}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Style: </span>
                    <span className="text-gray-600">{venue.style}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Key Features:</h4>
                  <div className="flex flex-wrap gap-1">
                    {venue.features.map((feature, index) => (
                      <span key={index} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Planning Your Wedding?
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            We'd love to help you capture your special day, whether it's at one of these venues or anywhere else in Northern California.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/quote"
              className="px-8 py-3 bg-gray-900 text-white font-light tracking-wide hover:bg-gray-800 transition-colors duration-300"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3 border border-gray-900 text-gray-900 font-light tracking-wide hover:bg-gray-900 hover:text-white transition-colors duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
